import type { StatusMeta, WorkQueueGroup } from '../../services/workQueue';
import { IMPLEMENTED_PHASES, IMPLEMENTED_STATUSES, PHASES } from './constants.ts';
import { possibleOwners } from './stepOwners.ts';

// Counts DTO plus the taxonomy fields that only arrive via meta.
export type StepItem = WorkQueueGroup & { order: number; owners: string[] };

export interface PhaseSection {
    phase: string;
    items: StepItem[];
    totalCount: number;
}

export function buildPhaseSections(
    groups: WorkQueueGroup[],
    statuses: StatusMeta[] | undefined,
    metaPhases: string[] | undefined,
    teamFilter: string[]
): PhaseSection[] {
    const countsByKey = new Map(groups.map((g) => [g.key, g]));
    const phaseOrder =
        metaPhases && metaPhases.length > 0 ? metaPhases : (PHASES as readonly string[]);
    const teamSet = new Set(teamFilter);
    const allStatuses = statuses ?? [];

    return (
        phaseOrder
            .map((phase) => {
                const phaseImpl = IMPLEMENTED_PHASES.has(phase);
                const items: StepItem[] = allStatuses
                    .filter((s) => s.phase === phase)
                    .filter((s) => phaseImpl || IMPLEMENTED_STATUSES.has(s.id))
                    .filter(
                        (s) => teamSet.size === 0 || possibleOwners(s).some((t) => teamSet.has(t))
                    )
                    .filter((s) => !s.deprecated || (countsByKey.get(s.id)?.count ?? 0) > 0)
                    .sort((a, b) => a.order - b.order)
                    .map((s) => {
                        const live = countsByKey.get(s.id);
                        const base =
                            live ??
                            ({
                                key: s.id,
                                phase: s.phase,
                                team: s.team,
                                action: s.action,
                                count: 0,
                                overdue: 0,
                                stale: 0
                            } satisfies WorkQueueGroup);
                        return { ...base, order: s.order, owners: possibleOwners(s) };
                    });
                const totalCount = items.reduce((sum, g) => sum + g.count, 0);
                return { phase, items, totalCount };
            })
            // Drop phases that end up empty (nothing implemented yet, or every step
            // filtered out by the team selection).
            .filter((section) => section.items.length > 0)
    );
}
